import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, tokenStorage } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  const bootstrap = useCallback(async () => {
    const token = tokenStorage.get();
    if (!token) {
      setUser(null);
      setChecking(false);
      return;
    }
    try {
      const me = await api.me();
      setUser(me);
    } catch {
      tokenStorage.clear();
      setUser(null);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = async (email, password) => {
    const data = await api.login(email, password);
    tokenStorage.set(data.access_token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.logout();
    } catch {
      /* ignore */
    }
    tokenStorage.clear();
    setUser(null);
  };

  const refreshMe = useCallback(async () => {
    try {
      const me = await api.me();
      setUser(me);
      return me;
    } catch {
      return null;
    }
  }, []);

  const helpers = useMemo(() => {
    const perms = new Set(user?.permissions || []);
    const isAdmin = user?.role === "admin";
    const hasPerm = (code) => {
      if (!user) return false;
      if (isAdmin) return true;
      return perms.has(code);
    };
    const hasAnyPerm = (...codes) => {
      if (!user) return false;
      if (isAdmin) return true;
      return codes.some((c) => perms.has(c));
    };
    return { hasPerm, hasAnyPerm, isAdmin };
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        user,
        checking,
        login,
        logout,
        refresh: bootstrap,
        refreshMe,
        ...helpers,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
